import { FilterGroups, ExtractedFilters, ContentFilterTag } from "./types";

export const toCamelCase = (str: string) =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

export function extractFilters(
  filters: any[] | undefined,
  allLabel: string
): ExtractedFilters {
  if (!filters) {
    return {
      sidebarFilterTags: [],
      topics: null,
    };
  }

  const topicsFilter = filters.find((f) => f.id === FilterGroups.Topics);
  const sidebarFilterTags = filters.filter((f) => f.id !== FilterGroups.Topics);

  let topics: ContentFilterTag | null = null;
  if (topicsFilter) {
    topics = {
      ...topicsFilter,
      options: [
        {
          __typename: "SelectInputOption",
          id: "all",
          label: allLabel,
          value: "all",
        },
        ...topicsFilter.options,
      ],
    };
  }

  return {
    sidebarFilterTags,
    topics,
  };
}

export function extractSelectedFilters(
  activeFilters: Record<string, string[]>
) {
  const selectedTopics = activeFilters["topics"] || [];
  const selectedSidebarFilterTags = Object.fromEntries(
    Object.entries(activeFilters).filter(([key]) => key !== "topics")
  );

  return {
    selectedTopics,
    selectedSidebarFilterTags,
  };
}

export function processSearchParams(
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams, options?: any) => void
) {
  const newUrlFilters: Record<string, string[]> = {};
  const newProcessedFilters: Record<string, string[]> = {};
  const validFilterKeys = Object.values(FilterGroups);

  searchParams.forEach((value, key) => {
    if (
      key !== "tab" &&
      key !== "search" &&
      validFilterKeys.includes(key as FilterGroups)
    ) {
      const camelKey = toCamelCase(key);
      if (value === "all") {
        newProcessedFilters[camelKey] = [];
      } else {
        newProcessedFilters[camelKey] = value.split(",");
      }
      newUrlFilters[key] = value.split(",");
    }
  });

  if (!newUrlFilters["topics"]) {
    newUrlFilters["topics"] = ["all"];
    if (!searchParams.get("topics")) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("topics", "all");
      setSearchParams(newSearchParams, { replace: true });
    }
  }

  return {
    urlFilters: newUrlFilters,
    processedFilters: newProcessedFilters,
  };
}

export function hasActiveFiltersUtil(
  urlFilters: Record<string, string[]>
): boolean {
  return Object.entries(urlFilters).some(([key, values]) => {
    return values.some((value) => value !== "all");
  });
}

// Formats filter object into sorted array of "category:option" strings; Excludes "all" values and sorts alphabetically
export function formatAppliedFilters(
  filters: Record<string, string[]>
): string[] {
  return Object.entries(filters)
    .flatMap(([category, values]) => {
      const filteredValues = values.filter((value) => value !== "all");
      return filteredValues.map((option) => `${category}:${option}`);
    })
    .sort();
}

// Extracts filter params from URLSearchParams, excluding tab and search; Converts comma-separated values into arrays
export function extractFiltersFromSearchParams(
  searchParams: URLSearchParams
): Record<string, string[]> {
  const filters: Record<string, string[]> = {};
  const validFilterKeys = Object.values(FilterGroups);

  searchParams.forEach((value, key) => {
    if (
      key !== "tab" &&
      key !== "search" &&
      validFilterKeys.includes(key as FilterGroups)
    ) {
      filters[key] = value.split(",");
    }
  });

  return filters;
}

export function getTrackingParamsFromUrl(): {
  appliedFilters: string[];
  searchTerm: string;
} {
  const searchParams = new URLSearchParams(window.location.search);
  const filters = extractFiltersFromSearchParams(searchParams);
  const appliedFilters = formatAppliedFilters(filters);
  const searchTerm = searchParams.get("search") || "";

  return {
    appliedFilters,
    searchTerm,
  };
}
